import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Home as HomeIcon,
  MapPin,
  Loader2,
  CheckCircle2,
  Search,
  ArrowRight,
  Info,
  Sparkles,
  Shield,
  Building2,
  Ruler,
  Calendar,
  Zap,
  TrendingUp,
  Users,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api } from "../../lib/api";
import { useSeo } from "../../lib/useSeo";
import { trackEvent as trackAction } from "../../lib/analytics";
import { loadGA, loadMetaPixel, trackConversion } from "../../lib/marketingTracking";

const PROPERTY_TYPES = [
  { v: "appartement", l: "Appartement" },
  { v: "benedenwoning", l: "Benedenwoning" },
  { v: "bovenwoning", l: "Bovenwoning" },
  { v: "tussenwoning", l: "Tussenwoning" },
  { v: "hoekwoning", l: "Hoekwoning" },
  { v: "twee_onder_een_kap", l: "2-onder-1-kapwoning" },
  { v: "vrijstaand", l: "Vrijstaande woning" },
  { v: "herenhuis", l: "Herenhuis" },
  { v: "overig", l: "Anders" },
];

const CONDITION_OPTS = [
  { v: "uitstekend", l: "Uitstekend — recent gerenoveerd" },
  { v: "goed", l: "Goed — normaal onderhoud" },
  { v: "redelijk", l: "Redelijk — enkele verbeteringen nodig" },
  { v: "matig", l: "Matig — grote renovatie nodig" },
];

const FAQS = [
  { q: "Krijg ik meteen een euro-bedrag?", a: "Standaard niet. WoningAlert laat alleen een geautomatiseerde INDICATIE zien wanneer je ons zelf een WOZ-waarde of recente aankoopprijs geeft. Die indicatie is gebaseerd op de officiële CBS-prijsindex Bestaande Koopwoningen (PBK) en is géén taxatie — CBS-cijfers zijn landelijke/regionale gemiddelden en bepalen niet de werkelijke waarde van jouw specifieke woning. Voor een echte waarde raden we een gratis waardebepaling door een lokale makelaar aan." },
  { q: "Waarom is dit alleen een indicatie en geen taxatie?", a: "De CBS Prijsindex Bestaande Koopwoningen volgt de prijsontwikkeling van ALLE verkochte woningen in een regio. Dat zegt iets over de markt, maar niet alles over jouw woning. Staat, locatie binnen de wijk, renovaties, energielabel, perceel, tuin op zuid, een nieuw dak — dat soort dingen zie je niet terug in een landelijk gemiddelde. Een lokale makelaar kijkt wél naar die individuele kenmerken." },
  { q: "Wat is het verschil tussen het WOZ-belastingjaar en de WOZ-waardepeildatum?", a: "Je WOZ-beschikking van bijvoorbeeld 2026 heeft als peildatum 1 januari 2025. De waarde die erop staat, is dus de geschatte marktwaarde van jouw woning op 1-1-2025 — niet van vandaag. Daarom gebruiken we deze peildatum (en niet het belastingjaar) als startpunt voor de CBS-indexering." },
  { q: "Is deze dienst echt gratis?", a: "Ja. Geen account, geen abonnement, geen betaling. Ook de persoonlijke waardebepaling door een aangesloten makelaar is vrijblijvend." },
  { q: "Welke bronnen gebruikt WoningAlert?", a: "Adresgegevens (bouwjaar, oppervlakte, type) uit de officiële Basisregistratie Adressen en Gebouwen (BAG) van het Kadaster. Energielabel uit EP-Online (RVO). Prijsontwikkeling uit CBS Open Data. Alle gebruikte bronnen worden expliciet bij je resultaat getoond." },
  { q: "Waarom tonen jullie geen bandbreedte zoals andere sites?", a: "Een onderbouwde bandbreedte vereist een gevalideerd Automated Valuation Model (AVM) met recente vergelijkbare verkopen. Dat bouwen we stap voor stap uit. Zolang we het niet statistisch kunnen onderbouwen, kiezen we bewust voor eerlijkheid: liever geen cijfer dan een cijfer dat niet klopt." },
  { q: "Wat gebeurt er met mijn gegevens?", a: "Je adres en voorkeur worden pas opgeslagen als je zelf een persoonlijke waardebepaling of een e-mailrapport aanvraagt. We delen je gegevens alleen met een geselecteerde makelaar nadat jij expliciet toestemming geeft. Zie onze privacyverklaring voor details." },
];

const WHY_FACTORS = [
  { icon: TrendingUp, title: "Marktontwikkeling", body: "Vraag en aanbod in jouw gemeente bepalen voor een groot deel de prijsontwikkeling. CBS en Kadaster publiceren hier maandelijks over." },
  { icon: Zap, title: "Energielabel", body: "Een duurzaam label (A of beter) verhoogt vaak de waarde en de maximale hypotheek. Een slecht label (E, F, G) drukt juist." },
  { icon: Ruler, title: "Oppervlakte & indeling", body: "Woonoppervlakte, perceelgrootte en het aantal (slaap)kamers zijn de belangrijkste basisfactoren." },
  { icon: Shield, title: "Staat & onderhoud", body: "Recente renovaties, de staat van keuken/badkamer en isolatie wegen zwaar in een persoonlijke waardebepaling." },
];

const normalizePostcode = (s) => (s || "").replace(/\s+/g, "").toUpperCase();

export const WoningwaardePage = () => {
  useSeo({
    title: "Wat is mijn woning waard? — Gratis waardecheck · WoningAlert",
    description: "Bereken gratis en vrijblijvend de geschatte marktwaarde van jouw woning. Officiële bronnen (BAG, EP-Online, CBS) + persoonlijke waardebepaling door een lokale makelaar.",
    path: "/woningwaarde",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
    ],
  });

  const [step, setStep] = useState(0);
  const [config, setConfig] = useState({ avm_enabled: false, meta_pixel_id: null, ga_measurement_id: null });
  const [addr, setAddr] = useState({ postcode: "", house_number: "", house_letter: "" });
  const [lookup, setLookup] = useState(null);
  const [looking, setLooking] = useState(false);
  const [features, setFeatures] = useState({
    property_type: "",
    bedrooms: "",
    bathrooms: "",
    plot_m2: "",
    has_garden: null,
    has_balcony: null,
    has_parking: null,
    has_garage: null,
    vve_contribution: "",
    erfpacht: null,
    condition: "",
    kitchen_condition: "",
    bathroom_condition: "",
    insulation: null,
    double_glazing: null,
    solar_panels: null,
    heat_pump: null,
    last_renovation_year: "",
  });
  const [lead, setLead] = useState({ name: "", email: "", phone: "", timeline: "", has_agent: null, appointment_pref: "", message: "", consent: false });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    trackAction("woningwaarde_page_view", {});
    api("/woningwaarde/config").then((c) => {
      setConfig(c);
      if (c.meta_pixel_id) loadMetaPixel(c.meta_pixel_id);
      if (c.ga_measurement_id) loadGA(c.ga_measurement_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const propertyTypeIsApt = ["appartement", "benedenwoning", "bovenwoning"].includes(features.property_type);

  const runLookup = async () => {
    const pc = normalizePostcode(addr.postcode);
    if (!/^\d{4}[A-Z]{2}$/.test(pc)) return toast.error("Vul een geldige postcode in (bv. 1012AB).");
    if (!addr.house_number) return toast.error("Vul je huisnummer in.");
    setLooking(true);
    try {
      trackAction("woningwaarde_start_calc", { postcode: pc });
      trackConversion("WoningwaardeStart");
      const r = await api("/woningwaarde/lookup", {
        method: "POST",
        body: JSON.stringify({ postcode: pc, house_number: Number(addr.house_number), house_letter: addr.house_letter || null }),
      });
      setLookup(r);
      if (r.building?.property_type && !features.property_type) {
        setFeatures((p) => ({ ...p, property_type: r.building.property_type === "woning" ? "" : "" }));
      }
      setStep(1);
    } catch (e) {
      toast.error(e.message || "Kon dit adres niet opzoeken.");
    } finally { setLooking(false); }
  };

  const submitLead = async () => {
    if (!lead.name || !lead.email) return toast.error("Vul minimaal je naam en e-mail in.");
    if (!lead.consent) return toast.error("Toestemming is verplicht om de aanvraag te versturen.");
    setLeadSubmitting(true);
    try {
      trackAction("woningwaarde_lead_submit", {});
      trackConversion("Lead", { content_name: "Woningwaarde — persoonlijke waardebepaling" });
      await api("/woningwaarde/lead", {
        method: "POST",
        body: JSON.stringify({
          ...lead,
          postcode: normalizePostcode(addr.postcode),
          house_number: Number(addr.house_number),
          house_letter: addr.house_letter || null,
          property_data: {
            address: lookup?.address || null,
            building: lookup?.building || null,
            features,
          },
        }),
      });
      setLeadDone(true);
      trackAction("woningwaarde_lead_success", {});
    } catch (e) {
      toast.error(e.message || "Verzenden mislukt.");
    } finally { setLeadSubmitting(false); }
  };

  const progress = useMemo(() => Math.round(((step + 1) / 4) * 100), [step]);
  const found = lookup?.found;

  return (
    <div ref={topRef}>
      {/* HERO */}
      {step === 0 && (
        <section className="relative overflow-hidden bg-gradient-to-br from-navy via-navy to-brandblue text-white">
          <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-amber-200">
              <Sparkles size={13} /> Gratis & vrijblijvend
            </div>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl">
              Wat is jouw woning waard?
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-white/80">
              Bereken gratis en vrijblijvend de geschatte marktwaarde van jouw woning. Officiële bronnen, geen verkoopdruk, en direct contact met een lokale makelaar.
            </p>
            <div className="mt-8 rounded-3xl bg-white p-6 text-navy shadow-2xl sm:p-8" data-testid="ww-address-card">
              <div className="grid gap-3 sm:grid-cols-[1fr_120px_120px_auto]">
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Postcode</Label>
                  <Input value={addr.postcode} onChange={(e) => setAddr({ ...addr, postcode: e.target.value })} placeholder="1012AB" autoComplete="postal-code" className="mt-1 rounded-xl text-base" data-testid="ww-postcode" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Huisnr.</Label>
                  <Input value={addr.house_number} onChange={(e) => setAddr({ ...addr, house_number: e.target.value.replace(/[^0-9]/g, "") })} placeholder="42" inputMode="numeric" className="mt-1 rounded-xl text-base" data-testid="ww-housenr" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500">Toev.</Label>
                  <Input value={addr.house_letter} onChange={(e) => setAddr({ ...addr, house_letter: e.target.value })} placeholder="A / bis" className="mt-1 rounded-xl text-base" data-testid="ww-letter" />
                </div>
                <div className="flex items-end">
                  <Button onClick={runLookup} disabled={looking} className="w-full rounded-xl bg-navy py-6 text-base text-amber-300 hover:bg-navy/90 sm:w-auto" data-testid="ww-lookup-btn">
                    {looking ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Search size={16} className="mr-2" />}
                    Vind mijn woning
                  </Button>
                </div>
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
                <Info size={12} /> We gebruiken officiële BAG-data van het Kadaster — geen schattingen, geen verkoopdruk.
              </p>
            </div>
          </div>
          <div className="absolute right-[-10%] top-[-10%] hidden h-96 w-96 rounded-full bg-amber-400/10 blur-3xl md:block" aria-hidden="true" />
        </section>
      )}

      {/* WIZARD */}
      {step > 0 && (
        <section className="bg-slate-50 py-10">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <div className="mb-4 flex items-center justify-between">
              <button onClick={() => setStep(Math.max(0, step - 1))} className="text-sm text-slate-500 hover:text-navy" data-testid="ww-back">← Terug</button>
              <span className="text-xs font-semibold text-slate-400">Stap {step} van 3</span>
            </div>
            <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
              <div className="h-full bg-brandblue transition-all" style={{ width: `${progress}%` }} />
            </div>

            {/* STEP 1: show BAG findings + corrections */}
            {step === 1 && (
              <div className="rounded-3xl bg-white p-6 shadow-sm" data-testid="ww-step-found">
                <h2 className="font-display text-2xl font-extrabold text-navy">Wat we hebben gevonden</h2>
                {!found ? (
                  <div className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
                    We konden dit adres niet matchen in de officiële BAG-registratie. Controleer de postcode en het huisnummer, of ga toch verder met een gratis persoonlijke waardebepaling.
                  </div>
                ) : (
                  <>
                    <div className="mt-4 rounded-2xl border border-success/30 bg-success/5 p-4">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-success" />
                        <div className="min-w-0">
                          <div className="font-display text-lg font-bold text-navy">
                            {lookup.address.street} {lookup.address.house_number}{lookup.address.house_letter ? ` ${lookup.address.house_letter}` : ""}
                          </div>
                          <div className="text-sm text-slate-600">{lookup.address.postcode} {lookup.address.city}</div>
                        </div>
                      </div>
                    </div>
                    <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                      {lookup.building.bouwjaar && <Fact icon={Calendar} label="Bouwjaar" value={lookup.building.bouwjaar} />}
                      {lookup.building.surface_m2 && <Fact icon={Ruler} label="Woonoppervlakte (BAG)" value={`${lookup.building.surface_m2} m²`} />}
                      {lookup.building.energy_label && <Fact icon={Zap} label="Energielabel" value={lookup.building.energy_label} />}
                      {lookup.building.property_type && <Fact icon={Building2} label="Type (BAG)" value={lookup.building.property_type} />}
                    </ul>
                    <p className="mt-3 text-xs text-slate-500">Bron: {(lookup.sources || []).map((s) => s.name).join(" · ")}</p>
                  </>
                )}
                <div className="mt-6 flex justify-end">
                  <Button onClick={() => setStep(2)} className="rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="ww-to-step2">
                    Verder <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2: slim additional questions */}
            {step === 2 && (
              <div className="rounded-3xl bg-white p-6 shadow-sm" data-testid="ww-step-features">
                <h2 className="font-display text-2xl font-extrabold text-navy">Aanvullende kenmerken</h2>
                <p className="mt-1 text-sm text-slate-500">Deze gegevens helpen de makelaar bij jouw persoonlijke waardebepaling.</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <Field label="Woningtype">
                    <Select value={features.property_type} onValueChange={(v) => setFeatures({ ...features, property_type: v })}>
                      <SelectTrigger className="rounded-xl" data-testid="ww-ptype"><SelectValue placeholder="Kies..." /></SelectTrigger>
                      <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Aantal slaapkamers">
                    <Input type="number" min="0" max="20" value={features.bedrooms} onChange={(e) => setFeatures({ ...features, bedrooms: e.target.value })} className="rounded-xl" />
                  </Field>
                  <Field label="Aantal badkamers">
                    <Input type="number" min="0" max="10" value={features.bathrooms} onChange={(e) => setFeatures({ ...features, bathrooms: e.target.value })} className="rounded-xl" />
                  </Field>
                  {!propertyTypeIsApt && (
                    <Field label="Perceel (m²)">
                      <Input type="number" min="0" value={features.plot_m2} onChange={(e) => setFeatures({ ...features, plot_m2: e.target.value })} className="rounded-xl" />
                    </Field>
                  )}
                  <Field label="Tuin?">
                    <YesNo value={features.has_garden} onChange={(v) => setFeatures({ ...features, has_garden: v })} />
                  </Field>
                  {propertyTypeIsApt && (
                    <>
                      <Field label="Balkon?">
                        <YesNo value={features.has_balcony} onChange={(v) => setFeatures({ ...features, has_balcony: v })} />
                      </Field>
                      <Field label="VvE-bijdrage per maand">
                        <Input type="number" min="0" value={features.vve_contribution} onChange={(e) => setFeatures({ ...features, vve_contribution: e.target.value })} placeholder="€" className="rounded-xl" />
                      </Field>
                    </>
                  )}
                  <Field label="Parkeerplaats?">
                    <YesNo value={features.has_parking} onChange={(v) => setFeatures({ ...features, has_parking: v })} />
                  </Field>
                  <Field label="Garage?">
                    <YesNo value={features.has_garage} onChange={(v) => setFeatures({ ...features, has_garage: v })} />
                  </Field>
                  <Field label="Erfpacht?">
                    <YesNo value={features.erfpacht} onChange={(v) => setFeatures({ ...features, erfpacht: v })} />
                  </Field>
                  <Field label="Algemene staat">
                    <Select value={features.condition} onValueChange={(v) => setFeatures({ ...features, condition: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies..." /></SelectTrigger>
                      <SelectContent>{CONDITION_OPTS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Zonnepanelen?">
                    <YesNo value={features.solar_panels} onChange={(v) => setFeatures({ ...features, solar_panels: v })} />
                  </Field>
                  <Field label="Warmtepomp?">
                    <YesNo value={features.heat_pump} onChange={(v) => setFeatures({ ...features, heat_pump: v })} />
                  </Field>
                  <Field label="Dubbel of driedubbel glas?">
                    <YesNo value={features.double_glazing} onChange={(v) => setFeatures({ ...features, double_glazing: v })} />
                  </Field>
                  <Field label="Jaar van laatste grote renovatie">
                    <Input type="number" min="1800" max="2026" value={features.last_renovation_year} onChange={(e) => setFeatures({ ...features, last_renovation_year: e.target.value })} className="rounded-xl" />
                  </Field>
                </div>
                <div className="mt-6 flex justify-end">
                  <Button onClick={() => { setStep(3); trackAction("woningwaarde_complete_calc", {}); trackConversion("WoningwaardeComplete"); }} className="rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="ww-to-result">
                    Toon mijn resultaat <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: result — NO euro, NO bandwidth. Honest + lead CTA. */}
            {step === 3 && !leadDone && (
              <div className="space-y-4" data-testid="ww-step-result">
                <div className="rounded-3xl bg-white p-6 shadow-sm">
                  <h2 className="font-display text-2xl font-extrabold text-navy">Jouw woning in kaart</h2>
                  {found && (
                    <div className="mt-2 text-sm text-slate-600">
                      {lookup.address.street} {lookup.address.house_number}{lookup.address.house_letter ? ` ${lookup.address.house_letter}` : ""}, {lookup.address.postcode} {lookup.address.city}
                    </div>
                  )}
                  <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                    {found && lookup.building.bouwjaar && <Fact icon={Calendar} label="Bouwjaar" value={lookup.building.bouwjaar} />}
                    {found && lookup.building.surface_m2 && <Fact icon={Ruler} label="Woonoppervlakte" value={`${lookup.building.surface_m2} m²`} />}
                    {found && lookup.building.energy_label && <Fact icon={Zap} label="Energielabel" value={lookup.building.energy_label} />}
                    {features.property_type && <Fact icon={Building2} label="Type (door jou opgegeven)" value={PROPERTY_TYPES.find((t) => t.v === features.property_type)?.l} />}
                    {features.condition && <Fact icon={Shield} label="Staat" value={CONDITION_OPTS.find((c) => c.v === features.condition)?.l} />}
                  </ul>
                  <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
                    <div className="flex items-start gap-2">
                      <Info size={16} className="mt-0.5 shrink-0" />
                      <div>
                        <strong>Dit is géén taxatie — alleen een indicatie.</strong>{" "}
                        {lookup?.disclaimer_nl || "We tonen alleen een geautomatiseerde indicatie wanneer we die kunnen onderbouwen met jouw WOZ-waarde of recente aankoopprijs én de officiële CBS-prijsindex. CBS-cijfers zijn landelijke/regionale gemiddelden en bepalen niet de werkelijke waarde van een individuele woning. Staat, locatie binnen de wijk, renovaties en energielabel kunnen de werkelijke waarde flink laten afwijken."}
                      </div>
                    </div>
                  </div>
                  {lookup?.market_context?.avg_sale_price && (
                    <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-xs text-slate-600" data-testid="ww-market-context">
                      <div className="flex items-center gap-2 font-semibold text-slate-700">
                        <TrendingUp size={14} /> Algemene marktinformatie — {lookup.market_context.municipality}
                      </div>
                      <div className="mt-1.5">
                        Gemiddelde verkoopprijs bestaande koopwoningen (CBS, {lookup.market_context.avg_sale_price.period?.slice(0, 4) || "recent"}): <strong>€{lookup.market_context.avg_sale_price.value.toLocaleString("nl-NL")}</strong>.
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">{lookup.market_context.label}</div>
                    </div>
                  )}
                </div>

                {/* Lead form */}
                <div className="rounded-3xl border border-brandblue/30 bg-brandblue-light/40 p-6" data-testid="ww-lead-form">
                  <div className="flex items-center gap-2 text-brandblue">
                    <Users size={16} />
                    <span className="text-xs font-semibold uppercase tracking-wide">Volgende stap</span>
                  </div>
                  <h3 className="mt-1 font-display text-xl font-extrabold text-navy">Wil je weten wat jouw woning écht kan opleveren?</h3>
                  <p className="mt-1 text-sm text-slate-600">Een lokale makelaar kent de staat van jouw woning en de buurt. Vraag gratis en vrijblijvend een persoonlijke waardebepaling aan.</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <Input placeholder="Naam" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="rounded-xl" data-testid="ww-lead-name" />
                    <Input placeholder="E-mail" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="rounded-xl" data-testid="ww-lead-email" />
                    <Input placeholder="Telefoon (optioneel)" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="rounded-xl" data-testid="ww-lead-phone" />
                    <Select value={lead.timeline} onValueChange={(v) => setLead({ ...lead, timeline: v })}>
                      <SelectTrigger className="rounded-xl" data-testid="ww-lead-timeline"><SelectValue placeholder="Wanneer verkopen?" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-3">0–3 maanden</SelectItem>
                        <SelectItem value="3-6">3–6 maanden</SelectItem>
                        <SelectItem value="6-12">6–12 maanden</SelectItem>
                        <SelectItem value="orienterend">Oriënterend</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea placeholder="Bericht (optioneel)" value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} rows={3} className="mt-3 rounded-xl" />
                  <label className="mt-3 flex items-start gap-2 text-xs text-slate-600" data-testid="ww-lead-consent-label">
                    <input type="checkbox" checked={lead.consent} onChange={(e) => setLead({ ...lead, consent: e.target.checked })} className="mt-0.5 h-4 w-4" data-testid="ww-lead-consent" />
                    <span>Ik geef toestemming om mijn gegevens te delen met een aangesloten makelaar voor een vrijblijvende waardebepaling. Zie onze <Link to="/privacy" className="text-brandblue underline">privacyverklaring</Link>.</span>
                  </label>
                  <Button
                    onClick={submitLead}
                    disabled={leadSubmitting || !lead.consent || !lead.name || !lead.email}
                    className="mt-4 w-full rounded-xl bg-success py-6 text-base hover:bg-success/90 sm:w-auto"
                    data-testid="ww-lead-submit"
                  >
                    {leadSubmitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                    Gratis waardebepaling aanvragen <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && leadDone && (
              <div className="rounded-3xl bg-success/5 border border-success/30 p-8 text-center" data-testid="ww-lead-success">
                <CheckCircle2 size={44} className="mx-auto text-success" />
                <h3 className="mt-3 font-display text-2xl font-extrabold text-navy">Bedankt — we nemen snel contact op</h3>
                <p className="mt-2 text-slate-600">Een lokale makelaar neemt binnen 1-2 werkdagen vrijblijvend contact op voor jouw gratis waardebepaling.</p>
                <Button asChild variant="outline" className="mt-6 rounded-xl">
                  <Link to="/">Terug naar WoningAlert</Link>
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* EXPLAINER / WHY / FAQ — only when at start */}
      {step === 0 && (
        <>
          <section className="bg-white py-16">
            <div className="mx-auto max-w-5xl px-4 sm:px-6">
              <h2 className="font-display text-3xl font-extrabold text-navy">Hoe werkt onze waardebepaling?</h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-3">
                <Step n="1" title="Vind jouw woning" body="We matchen je adres met de officiële BAG-registratie en halen bouwjaar, oppervlakte en type op." />
                <Step n="2" title="Vul kenmerken aan" body="Jij vult de informatie aan die geen enkele openbare bron weet: staat, keuken/badkamer, renovaties." />
                <Step n="3" title="Persoonlijke waardebepaling" body="Een lokale makelaar beoordeelt alles samen en komt vrijblijvend met een realistische waarde." />
              </div>
            </div>
          </section>
          <section className="bg-slate-50 py-16">
            <div className="mx-auto max-w-5xl px-4 sm:px-6">
              <h2 className="font-display text-3xl font-extrabold text-navy">Wat bepaalt de waarde van jouw woning?</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {WHY_FACTORS.map((f) => (
                  <div key={f.title} className="rounded-2xl bg-white p-5 shadow-sm">
                    <f.icon size={22} className="text-brandblue" />
                    <h3 className="mt-3 font-display text-base font-bold text-navy">{f.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{f.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="bg-white py-16">
            <div className="mx-auto max-w-3xl px-4 sm:px-6">
              <h2 className="font-display text-3xl font-extrabold text-navy">Veelgestelde vragen</h2>
              <div className="mt-6 space-y-3" data-testid="ww-faq">
                {FAQS.map((f, i) => (
                  <details key={i} className="group rounded-2xl border border-border bg-white p-4 open:bg-slate-50">
                    <summary className="cursor-pointer list-none font-semibold text-navy">{f.q}</summary>
                    <p className="mt-2 text-sm text-slate-600">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
          <section className="bg-navy py-16 text-white">
            <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
              <HomeIcon size={32} className="mx-auto text-amber-300" />
              <h3 className="mt-3 font-display text-2xl font-extrabold">Klaar om te starten?</h3>
              <p className="mt-2 text-white/70">Scroll omhoog en vul je postcode en huisnummer in — binnen 1 minuut heb je een overzicht van jouw woning.</p>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

const Fact = ({ icon: Icon, label, value }) => (
  <li className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
    <Icon size={16} className="text-brandblue" />
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="truncate font-semibold text-navy">{value}</div>
    </div>
  </li>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const YesNo = ({ value, onChange }) => (
  <div className="flex gap-2">
    {[["Ja", true], ["Nee", false]].map(([l, v]) => (
      <button
        key={l}
        type="button"
        onClick={() => onChange(v)}
        className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition ${value === v ? "border-brandblue bg-brandblue text-white" : "border-border bg-white text-slate-600 hover:border-brandblue/50"}`}
      >{l}</button>
    ))}
  </div>
);

const Step = ({ n, title, body }) => (
  <div className="rounded-2xl bg-white p-5 shadow-sm">
    <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-navy font-display text-sm font-extrabold text-amber-300">{n}</div>
    <h3 className="mt-3 font-display text-base font-bold text-navy">{title}</h3>
    <p className="mt-1 text-sm text-slate-600">{body}</p>
  </div>
);

export default WoningwaardePage;
